import { useEffect } from 'react'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { IPaymentFormData, PaymentMethod } from '@typings'
import { PAYMENT_METHOD_OPTIONS, PAYMENT_LABELS } from 'src/constants/payment.constants'
import { formatValueToCurrency } from 'src/utilities/helpers/formatCentToAmount.helper'
import { OvFormInput, OvFormProvider, OvForm, OvCard } from '@components'

interface PaymentFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: IPaymentFormData) => Promise<void>
  defaultValues?: Partial<IPaymentFormData>
  maxAmount?: number
  loading?: boolean
}

export const PaymentForm = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  maxAmount,
  loading = false,
}: PaymentFormProps) => {
  const form = useForm<IPaymentFormData>({
    defaultValues: {
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer' as PaymentMethod,
      ...defaultValues,
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form

  useEffect(() => {
    if (open) {
      reset({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer' as PaymentMethod,
        ...defaultValues,
      })
    }
  }, [open, defaultValues, reset])

  const handleFormSubmit = async (data: IPaymentFormData) => {
    try {
      await onSubmit(data)
      reset()
      onClose()
    } catch (error) {
      console.error('Payment submission error:', error)
    }
  }

  const submitForm = () => {
    handleSubmit(handleFormSubmit)()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <OvFormProvider
        form={form}
        onSubmit={submitForm}
        loading={loading}
        enableNavigationBlocker={false}>
        <OvForm>
          <OvCard>
            <CardHeader title={PAYMENT_LABELS.TITLE} />
            <CardContent>
              <Stack spacing={2.5}>
              <OvFormInput
                type="currency"
                name="amount"
                label={PAYMENT_LABELS.AMOUNT}
                rules={{
                  required: 'Bedrag is verplicht',
                  min: { value: 1, message: 'Bedrag moet groter dan 0 zijn' },
                  max: maxAmount
                    ? {
                        value: maxAmount,
                        message: `Bedrag kan niet meer zijn dan ${formatValueToCurrency(maxAmount)}`,
                      }
                    : undefined,
                }}
                helperText={maxAmount ? `Max: ${formatValueToCurrency(maxAmount)}` : ''}
              />

            <Controller
              name="payment_date"
              control={control}
              rules={{ required: 'Betaaldatum is verplicht' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.PAYMENT_DATE}
                  type="date"
                  fullWidth
                  required
                  error={!!errors.payment_date}
                  helperText={errors.payment_date?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="payment_method"
              control={control}
              rules={{ required: 'Betaalwijze is verplicht' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.PAYMENT_METHOD}
                  select
                  fullWidth
                  required
                  error={!!errors.payment_method}
                  helperText={errors.payment_method?.message}>
                  {PAYMENT_METHOD_OPTIONS.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.REFERENCE}
                  fullWidth
                  error={!!errors.reference}
                  helperText={errors.reference?.message}
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.NOTES}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </Stack>
        </CardContent>
        <CardActions>
          <Button
            onClick={onClose}
            disabled={isSubmitting || loading}>
            {PAYMENT_LABELS.CANCEL}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || loading}>
            {PAYMENT_LABELS.SAVE}
          </Button>
        </CardActions>
        </OvCard>
        </OvForm>
      </OvFormProvider>
    </Dialog>
  )
}
